import { colors } from '@lmc/common/constants'
import { Responsibility, UserRole } from '@lmc/ts-common'
import { useRoleAccess } from 'components/AccessControl'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import { NAMESPACE } from 'locale'
import { teamTranslations } from 'locale/team'
import React, { type FunctionComponent, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { getNominative } from '../../../../common/utils'
import Field from '../../../fields'
import { LinkComponent } from '../../../generic'
import {
  CARE_MANAGEMENT_LABEL,
  CERNER_ROLE_OPTIONS,
  getPermissionBooleanByTemplate,
  getRoleFieldsProps,
  getSafeguardingPermissionValue,
  SAFEGUARDING_RADIO_OPTIONS,
  updatePermissionsForSafeguarding
} from './utils'

interface TeamMemberEditFormRolesPermissionsV2Props {
  features: {
    eMAR: boolean
  }
  fields: Record<string, any>
  formId: string
  preferredPeopleNominative: string
  style?: Record<string, any>
  gpConnectEnabled?: boolean
  cernerEnabled?: boolean
  initial?: Record<string, unknown>
  role?: UserRole
  availablePermissionsTemplates?: { id: string; label: string }[]
  testId?: string
  setFormField?: ({ key, value }: { key: string; value: any }) => void
}

const renderEmarAdvert = () => {
  return (
    <div style={styles.emarAdvertContainer}>
      <div style={styles.emarAdvertLabel}>Medication</div>
      <LinkComponent target="_blank" to="/settings/billing">
        eMAR Feature <span className="octicon octicon-chevron-right" />
      </LinkComponent>
    </div>
  )
}

const TeamMemberEditFormRolesPermissionsV2: FunctionComponent<
  TeamMemberEditFormRolesPermissionsV2Props
> = (props) => {
  const {
    features,
    fields,
    formId,
    preferredPeopleNominative,
    style,
    gpConnectEnabled,
    cernerEnabled,
    initial,
    role,
    availablePermissionsTemplates,
    testId,
    setFormField
  } = props

  const { t } = useTranslation(NAMESPACE.TEAM)

  const flags = useFeatureFlags({
    flagNames: ['safeguarding'] as const
  })?.data

  const flagsSafeguardingEnabled = flags?.safeguarding.enabled

  const isAdmin = useRoleAccess(UserRole.careHomeAdmin)

  const { ROLE_NOTE, ROLE_OPTIONS } = getRoleFieldsProps(
    initial,
    role,
    preferredPeopleNominative
  )

  const hasSeniorCarerSelected = fields.role === UserRole.seniorCarer
  const hasAdminSelected = fields.role === UserRole.careHomeAdmin

  const getTemplateNote = () => {
    if (!fields.permissionsTemplate || fields.permissionsTemplate === null) {
      return ''
    }

    return `What’s included:  \n • Create and edit ${getNominative(
      preferredPeopleNominative,
      'Residents'
    ).toLocaleLowerCase()} profiles  \n • View and edit Logs  \n • View Team Members and re-send invites  \n • Export home data via the Download Centre`
  }

  const { isPro, isOutstanding } = useHomeType()

  const safeguardingEnabled =
    flagsSafeguardingEnabled &&
    (isPro || isOutstanding) &&
    isAdmin &&
    hasAdminSelected

  const handleSafeguardingChange = (value: string | null) => {
    const shouldHavePermission = value === Responsibility.SafeguardingOfficer
    const newPermissions = updatePermissionsForSafeguarding(
      fields.permissions,
      shouldHavePermission
    )

    setFormField?.({ key: 'permissions', value: newPermissions })
  }

  const safeguardingValue = getSafeguardingPermissionValue(fields.permissions)

  return (
    <div
      style={style}
      data-testid={testId ? `${testId}-roles-permissions-container` : undefined}
    >
      <div style={{ paddingBottom: 20 }}>
        <div style={styles.formField}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              className="role-field"
              formId={formId}
              keys="role"
              label="Role"
              options={ROLE_OPTIONS}
              note={ROLE_NOTE}
              required
              type="radios"
              values={fields}
              data-testid={testId ? `${testId}-role-field` : undefined}
            />
          </Suspense>
        </div>
        {hasSeniorCarerSelected && (
          <div style={{ marginBottom: 15 }}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                values={{
                  ...fields,
                  ...(!fields?.permissionsTemplate
                    ? { permissionsTemplate: null }
                    : {})
                }}
                formId={formId}
                keys="permissionsTemplate"
                label="Care Management"
                isRequired
                data-testid={
                  testId ? `${testId}-care-management-field` : undefined
                }
                options={[
                  {
                    value: getPermissionBooleanByTemplate(
                      CARE_MANAGEMENT_LABEL,
                      availablePermissionsTemplates
                    )?.id,
                    label: 'Yes'
                  },
                  { value: null, label: 'No' }
                ]}
                type="radios"
                note={getTemplateNote()}
              />
            </Suspense>
          </div>
        )}
        {safeguardingEnabled && (
          <div style={{ marginBottom: 15 }}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                values={{
                  ...fields,
                  safeguardingOfficer: safeguardingValue
                }}
                formId={formId}
                keys="safeguardingOfficer"
                label={t(teamTranslations.SAFEGUARDING_OFFICER)}
                isRequired
                options={SAFEGUARDING_RADIO_OPTIONS}
                type="radios"
                note={t(teamTranslations.SAFEGUARDING_OFFICER_NOTE)}
                onChange={handleSafeguardingChange}
              />
            </Suspense>
          </div>
        )}
        {features.eMAR ? (
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formId={formId}
              keys="medicationPermission"
              label="Medication"
              options={DEFAULT_RADIO_OPTIONS}
              note={getNote(preferredPeopleNominative)}
              required
              type="radios"
              values={fields}
              data-testid={testId ? `${testId}-medication-field` : undefined}
            />
          </Suspense>
        ) : (
          renderEmarAdvert()
        )}
        {gpConnectEnabled && (
          <div style={{ marginTop: 15 }}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                formId={formId}
                keys="gpConnectPermission"
                label="GP Connect Access"
                options={DEFAULT_GP_CONNECT_OPTIONS}
                required
                type="radios"
                values={fields}
                data-testid={testId ? `${testId}-gp-connect-field` : undefined}
              />
            </Suspense>
          </div>
        )}
        {cernerEnabled && (
          <div style={{ marginTop: 15 }}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                formId={formId}
                keys="cernerRole"
                label="London Care Record Access Level"
                options={CERNER_ROLE_OPTIONS}
                note="Role as per NHS role based access control. It's mandatory for all staff to complete training for accessing the London Care Record."
                required
                type="select"
                values={fields}
                data-testid={testId ? `${testId}-cerner-role-field` : undefined}
              />
            </Suspense>
          </div>
        )}
      </div>
    </div>
  )
}

const getNote = (preferredPeopleNominative: string) =>
  `In the Carer App: Ability to sign medication.\nIn the Care Office: Ability to manage medication for all ${getNominative(
    preferredPeopleNominative,
    'Residents'
  )}.`

const DEFAULT_RADIO_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' }
]

const DEFAULT_GP_CONNECT_OPTIONS = [
  { value: true, label: 'Clinical Access' },
  { value: false, label: 'Non-Clinical Access' }
]

const styles = {
  emarAdvertContainer: {
    display: 'flex'
  },
  emarAdvertLabel: {
    fontWeight: 600,
    marginRight: 30,
    minWidth: 160
  },
  emarAdvertText: {
    color: colors.pink
  },
  formField: {
    paddingBottom: 20
  }
}

export default TeamMemberEditFormRolesPermissionsV2
